import React from "react";
import { Box, Typography } from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import SelectableChipList from "../SelectableChipList";

interface PbmPlan {
  memberUniqueId?: string;
  displayMemberUniqueId?: string;
  payerName?: string;
  planName?: string;
  groupId?: string;
  healthPlanId?: string;
  pbmParticipantId?: string;
  [key: string]: any;
}

interface PbmLookupProps {
  plans: (string | PbmPlan)[];
  primaryColor: string;
  onPbmSelect: (displayMemberUniqueId: string, memberUniqueId?: string) => void;
  label?: string;
  /** True on a past (non-latest) turn: render read-only. */
  disabled?: boolean;
}

const PbmLookup: React.FC<PbmLookupProps> = ({
  plans,
  primaryColor,
  onPbmSelect,
  label = "Select a Benefit Source:",
  disabled = false,
}) => {
  // The chip's selected VALUE must be the displayMemberUniqueId (what the follow-up message
  // carries); the id is the memberUniqueId (the RxHub key the backend consumes).
  const extractId = (plan: string | PbmPlan) => {
    if (typeof plan === "string") return undefined;
    const id = plan?.memberUniqueId;
    return id === undefined || id === null ? undefined : String(id);
  };

  // SelectableChipList reads item.text/name/value for the selection value — map display id onto it.
  const normalizedPlans = plans.map((plan) =>
    typeof plan === "string"
      ? plan
      : {
          ...plan,
          text: plan.displayMemberUniqueId || plan.memberUniqueId || "",
        }
  );

  const renderPbmLabel = (plan: string | PbmPlan): React.ReactNode => {
    if (typeof plan === "string") return plan;
    const display = plan.displayMemberUniqueId || plan.memberUniqueId || "";
    const subLine = [plan.payerName, plan.planName]
      .filter((p) => typeof p === "string" && p.trim().length > 0)
      .join(" • ");
    if (!subLine || subLine === display) return display;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {display}
        </Typography>
        <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
          {subLine}
        </Typography>
      </Box>
    );
  };

  return (
    <SelectableChipList
      items={normalizedPlans}
      primaryColor={primaryColor}
      onItemSelect={onPbmSelect}
      label={label}
      icon={<HealthAndSafetyIcon sx={{ fontSize: "16px !important" }} />}
      extractId={extractId}
      getItemLabel={renderPbmLabel}
      disabled={disabled}
    />
  );
};

export default PbmLookup;
